import { useEffect } from 'react';
import { Navigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { usePageContext } from '@hero/web-context';
import { useCurrentUser } from '@hero/hooks-shared';
import { BillingCheckoutScreen } from '@hero/web-components';
import { useSubscriptionMineApi, useSubscriptionUsageApi } from '@hero/api-hooks';
import type { SubscriptionGetUsageAPI } from '@hero/api';
import type { SubscriptionPublic } from '@hero/schema';
import {
  resolveDefaultBillingUnit,
  route,
  shouldRedirectFromBillingPlanPicker,
  useBillingCheckoutManager,
} from '@hero/web-hooks';

/**
 * Pay-subscription page mounted at `/billing/plan`. Shared
 * `<BillingCheckoutScreen />` for first-time checkout, trial
 * conversion, cancel re-subscribe, and overdue recovery.
 *
 * Orgs with a healthy paid subscription (`active` / `paused`) are
 * redirected to `/billing` — plan changes and seat bumps will land
 * here later behind explicit CTAs.
 *
 * Loads data here and hands it to {@link PlanPicker} rather than
 * calling the checkout manager directly: the manager seeds its plan
 * selection once, on mount, so it must not mount until the reads it
 * seeds from have landed.
 */
export default function BillingPlansPage() {
  const { t } = useTranslation('m-billing');
  const { setPageName } = usePageContext();
  const subscriptionQuery = useSubscriptionMineApi();
  const subscription = subscriptionQuery.data ?? null;
  // Which way of paying to lead with, for an org that has never
  // committed to one. A three-truck operator shipping weekly should not
  // land on a card quoting it fifteen seats.
  const usageQuery = useSubscriptionUsageApi();

  useEffect(() => {
    // Top-of-page chrome shows "Your Subscription" so the breadcrumb
    // reads as a section heading (not the page's primary verb). The
    // actual call-to-action live below as the screen's H1.
    setPageName(t('subscription.title'));
  }, [setPageName, t]);

  // Hold on a blank canvas while the subscription query resolves —
  // same guard as `useBillingOverviewManager` uses against flashing
  // the wrong branch on refresh (`data === undefined` ≠ "no sub").
  // A usage error is not fatal; the basis just falls back.
  if (
    subscriptionQuery.isPending ||
    subscriptionQuery.data === undefined ||
    subscriptionQuery.isError ||
    usageQuery.isPending
  ) {
    return null;
  }

  if (shouldRedirectFromBillingPlanPicker(subscription)) {
    return <Navigate to={route('billing')} replace />;
  }

  return <PlanPicker subscription={subscription} usage={usageQuery.data} />;
}

function PlanPicker({
  subscription,
  usage,
}: {
  subscription: SubscriptionPublic | null;
  usage: SubscriptionGetUsageAPI['data'] | undefined;
}) {
  const { t } = useTranslation('m-billing');
  const { orgId } = useCurrentUser();
  const checkout = useBillingCheckoutManager({
    orgId,
    subscription,
    defaultUnit: resolveDefaultBillingUnit({
      vehicleCount: usage?.vehicleCount,
      ordersThisPeriod: usage?.orderCount,
    }),
  });

  return (
    <div className="mt-6">
      <BillingCheckoutScreen
        // Reuses the same H1 / sub-copy as the no-subscription landing
        // — "Choose your plan" works for both first-time subscribers AND
        // existing customers swapping plans. Avoids the schizophrenic
        // "Change Plan" verb that hides the actual decision (which
        // plan?) behind a tense.
        title={t('checkout.landing.title')}
        description={t('checkout.landing.description')}
        planCode={checkout.planPicker.planCode}
        onPlanCodeChange={checkout.planPicker.setPlanCode}
        interval={checkout.planPicker.interval}
        onIntervalChange={checkout.planPicker.setInterval}
        committedVehicleQty={checkout.planPicker.committedVehicleQty}
        onCommittedVehicleQtyChange={checkout.planPicker.setCommittedVehicleQty}
        committedOrderQty={checkout.planPicker.committedOrderQty}
        onCommittedOrderQtyChange={checkout.planPicker.setCommittedOrderQty}
        resolvedPlanCode={checkout.resolvedPlanCode}
        currency={checkout.planPicker.currency}
        business={checkout.business}
        audience={checkout.audience}
        defaultUnit={checkout.defaultUnit}
        preview={checkout.preview}
        variant={checkout.variant}
        nextRenewalAt={checkout.nextRenewalAt}
        onPrimaryClick={checkout.submit}
        isSubmitting={checkout.isSubmitting}
        redirectOverlayOpen={checkout.isRedirecting}
      />
    </div>
  );
}
